import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Autocomplete,
    TextField,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    LocationCity as CityIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import cityService from '../../services/cityService';
import { useConfirm } from '../../contexts/FeedbackContext';
import DataTable from '../../components/common/DataTable';
import StatusChip from '../../components/common/StatusChip';

const CitiesSection = () => {
    const confirm = useConfirm();
    const [expanded, setExpanded] = useState(false);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [provinces, setProvinces] = useState([]);
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [citiesOptions, setCitiesOptions] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [citySearch, setCitySearch] = useState('');
    const [loadingCities, setLoadingCities] = useState(false);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [rowsPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchCities = async (currentPage = page, search = searchTerm) => {
        try {
            setLoading(true);
            setError('');
            const data = await cityService.getCities({
                page: currentPage + 1,
                search: search,
                active_only: 'true'
            });
            if (data && data.results) {
                setCities(data.results);
                setTotalCount(data.count);
            } else {
                setCities(data || []);
                setTotalCount(data ? data.length : 0);
            }
        } catch (err) {
            setError('Error al obtener la lista de ciudades.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (expanded) {
            const delayDebounce = setTimeout(() => {
                fetchCities(page, searchTerm);
            }, 300);
            return () => clearTimeout(delayDebounce);
        }
    }, [expanded, page, searchTerm]);

    useEffect(() => {
        if (openDialog) {
            cityService.getProvinces().then((data) => {
                setProvinces(data.results || data);
            }).catch(() => {
                setError('Error al cargar la lista de provincias.');
            });
            setSelectedProvince(null);
            setSelectedCity(null);
            setCitiesOptions([]);
        }
    }, [openDialog]);

    useEffect(() => {
        if (!selectedProvince) {
            setCitiesOptions([]);
            setSelectedCity(null);
            return;
        }
        setLoadingCities(true);
        cityService.searchCities(selectedProvince.id, citySearch)
            .then((data) => {
                setCitiesOptions(data.results || data);
            })
            .catch(() => {
                setError('Error al buscar ciudades.');
            })
            .finally(() => {
                setLoadingCities(false);
            });
    }, [selectedProvince, citySearch]);

    useEffect(() => {
        if (selectedCity && selectedProvince) {
            setSelectedPlace({
                name: selectedCity.name,
                province_id: selectedProvince.id,
                formatted_address: selectedCity.formatted_address || `${selectedCity.name}, ${selectedProvince.name}, Argentina`,
                latitude: selectedCity.latitude,
                longitude: selectedCity.longitude,
                place_id: selectedCity.place_id || selectedCity.external_id,
                external_id: selectedCity.external_id
            });
        } else {
            setSelectedPlace(null);
        }
    }, [selectedCity, selectedProvince]);

    const handleOpen = () => {
        setSelectedPlace(null);
        setError('');
        setOpenDialog(true);
    };

    const handleSave = async () => {
        if (!selectedPlace) {
            setError('Debe seleccionar una provincia y ciudad válidas.');
            return;
        }

        try {
            setError('');
            await cityService.createCity(selectedPlace);
            setSuccess(`Ciudad "${selectedPlace.name}" guardada con éxito.`);
            setOpenDialog(false);
            fetchCities(page, searchTerm);
        } catch (err) {
            setError(err?.response?.data?.error || 'Error al guardar la ciudad. Intente nuevamente.');
        }
    };

    const handleDelete = async (id) => {
        if (!(await confirm({ title: 'Eliminar ciudad', message: '¿Está seguro de que desea eliminar/desactivar esta ciudad?', confirmText: 'Eliminar', danger: true }))) {
            return;
        }
        try {
            setError('');
            await cityService.deleteCity(id);
            setSuccess('Ciudad eliminada exitosamente.');
            fetchCities(page, searchTerm);
        } catch (err) {
            setError('Error al eliminar la ciudad.');
        }
    };

    const handleToggleRepartos = async (city) => {
        const newValue = !city.is_available_for_trips;
        // Optimistic update
        setCities((prev) => prev.map((c) => (c.id === city.id ? { ...c, is_available_for_trips: newValue } : c)));
        try {
            await cityService.updateCity(city.id, { is_available_for_trips: newValue });
            setSuccess(`Ciudad "${city.name}" ${newValue ? 'habilitada' : 'deshabilitada'} para repartos.`);
        } catch (err) {
            // Revert
            setCities((prev) => prev.map((c) => (c.id === city.id ? { ...c, is_available_for_trips: !newValue } : c)));
            setError('Error al actualizar la visibilidad en repartos.');
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const columns = [
        { id: 'name', label: 'Nombre de la Ciudad', sx: { fontWeight: 600 } },
        { id: 'province', label: 'Provincia', render: (row) => row.province?.name || '-' },
        { id: 'formatted_address', label: 'Dirección Completa' },
        { id: 'latitude', label: 'Latitud', align: 'right' },
        { id: 'longitude', label: 'Longitud', align: 'right' },
        {
            id: 'is_available_for_trips',
            label: 'Repartos',
            render: (row) => {
                const active = !!row.is_available_for_trips;
                return (
                    <Chip
                        label={active ? "Habilitado" : "Deshabilitado"}
                        size="small"
                        onClick={() => handleToggleRepartos(row)}
                        sx={{
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            bgcolor: active ? '#e3f2fd' : '#ffebee',
                            color: active ? '#1976d2' : '#c62828',
                            border: `1px solid ${active ? '#90caf9' : '#ffcdd2'}`,
                            '&:hover': {
                                bgcolor: active ? '#bbdefb' : '#ffcdd2',
                            }
                        }}
                    />
                );
            }
        },
        {
            id: 'is_active',
            label: 'Estado',
            render: (row) => <StatusChip status={row.is_active} type="active" />
        },
    ];

    return (
        <Accordion
            expanded={expanded}
            onChange={(e, exp) => setExpanded(exp)}
            sx={{
                mb: 2,
                borderRadius: '0.75rem',
                border: '1px solid #d0dbe7',
                boxShadow: 'none',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#800020' }} />}
                sx={{
                    bgcolor: '#f6f7f8',
                    borderRadius: expanded ? '0.75rem 0.75rem 0 0' : '0.75rem',
                    '&:hover': { bgcolor: '#f0f0f0' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <CityIcon sx={{ color: '#800020' }} />
                    <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>Ciudades para Reparto</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpen}
                        sx={{ bgcolor: '#800020', '&:hover': { bgcolor: '#600010' } }}
                    >
                        Agregar Ciudad (GeoRef)
                    </Button>
                </Box>

                <DataTable
                    columns={columns}
                    rows={cities}
                    loading={loading}
                    searchTerm={searchTerm}
                    onSearchChange={(val) => { setSearchTerm(val); setPage(0); }}
                    searchPlaceholder="Buscar por nombre de ciudad..."
                    count={totalCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    actions={(row) => (
                        <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                />

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700 }}>Agregar Nueva Ciudad</DialogTitle>
                    <DialogContent sx={{ pt: 2, pb: 4 }}>
                        <Box sx={{ pt: 1, pb: 2 }}>
                            <Typography sx={{ color: '#4e7397', mb: 2, fontSize: '0.875rem' }}>
                                Seleccione una provincia y luego busque la ciudad deseada para habilitarla.
                            </Typography>
                            
                            <Autocomplete
                                id="province-select"
                                options={provinces}
                                getOptionLabel={(option) => option.name}
                                value={selectedProvince}
                                onChange={(event, newValue) => {
                                    setSelectedProvince(newValue);
                                    setSelectedCity(null);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Provincia"
                                        variant="outlined"
                                        fullWidth
                                        sx={{ mb: 2 }}
                                    />
                                )}
                            />

                            {selectedProvince && (
                                <Autocomplete
                                    id="city-select"
                                    options={citiesOptions}
                                    getOptionLabel={(option) => option.name}
                                    value={selectedCity}
                                    onChange={(event, newValue) => {
                                        setSelectedCity(newValue);
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        setCitySearch(newInputValue);
                                    }}
                                    loading={loadingCities}
                                    filterOptions={(x) => x}
                                    noOptionsText="No se encontraron ciudades cargadas para esta provincia"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Ciudad"
                                            variant="outlined"
                                            fullWidth
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            )}
                        </Box>
                        {selectedPlace && (
                            <Box sx={{ p: 2, bgcolor: '#f6f7f8', borderRadius: '0.5rem', border: '1px solid #d0dbe7', mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0e141b', mb: 0.5 }}>
                                    Confirmar Detalles:
                                </Typography>
                                <Typography sx={{ fontSize: '0.875rem', mb: 1 }}>
                                    <strong>Nombre:</strong> {selectedPlace.name} <br />
                                    <strong>Provincia:</strong> {selectedProvince?.name} <br />
                                    <strong>Dirección Formateada:</strong> {selectedPlace.formatted_address} <br />
                                    <strong>Coordenadas:</strong> {selectedPlace.latitude}, {selectedPlace.longitude}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 0 }}>
                        <Button onClick={() => setOpenDialog(false)} sx={{ color: '#4e7397', textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!selectedPlace}
                            sx={{ bgcolor: '#800020', '&:hover': { bgcolor: '#600010' }, textTransform: 'none', borderRadius: '0.5rem' }}
                        >
                            Confirmar y Guardar
                        </Button>
                    </DialogActions>
                </Dialog>
            </AccordionDetails>
        </Accordion>
    );
};

export default CitiesSection;
